import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Brand } from "./shared/Brand";
import { Footer } from "./sections/Footer";
import WhatsAppButton from "./WhatsAppButton";

const DRAFT_KEY = "ev_ga_draft";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTORS = ["Retail & Supermarkets", "Hospitality & Hotels", "Manufacturing", "Education & Schools", "Healthcare", "Logistics & Warehousing", "Airports & Transport", "Forecourts & Convenience", "Offices", "Data Centres", "Other"];
const REGIONS = ["United Kingdom", "Ireland", "Europe", "North America", "Middle East", "Asia-Pacific", "Africa", "Other"];
const FAC_TYPES = ["Single building", "Multi-building site", "Warehouse / Industrial", "Office", "Retail unit", "Mixed-use", "Other"];
const HEARD = ["Referral", "Search engine", "Social media", "Industry event", "Existing client", "Other"];
const CONTACT_PREF = ["Email", "Phone", "Either"];
const MAIN_CONCERNS = [
  "Rising energy costs",
  "Unexplained consumption",
  "High baseload / out-of-hours consumption",
  "Equipment efficiency",
  "Water consumption",
  "Carbon / ESG reporting",
  "Lack of visibility",
  "Equipment reliability / downtime",
  "Multi-site visibility",
  "Not sure — this is what we want the Gap Analysis to identify",
];

const STEPS = [
  {
    title: "Your Details",
    subtitle: "Who we'll be speaking with.",
    fields: [
      { name: "client_first_name", label: "First name", type: "text", required: true, half: true },
      { name: "client_last_name", label: "Last name", type: "text", half: true },
      { name: "position_role", label: "Position / role", type: "text", half: true },
      { name: "client_email", label: "Email", type: "email", required: true, half: true },
      { name: "direct_telephone", label: "Direct telephone", type: "tel", half: true },
      { name: "mobile", label: "Mobile", type: "tel", half: true },
      { name: "company_name", label: "Company / organisation name", type: "text", required: true },
      { name: "company_website", label: "Company website", type: "text", half: true },
      { name: "companies_house_no", label: "Companies House / registration no.", type: "text", half: true },
      { name: "industry_sector", label: "Industry sector", type: "select", options: SECTORS, required: true, half: true },
      { name: "region", label: "Region", type: "select", options: REGIONS, half: true },
      { name: "head_office_address", label: "Head office address", type: "text" },
    ],
  },
  {
    title: "Facility / Site",
    subtitle: "The building we'll be reviewing.",
    fields: [
      { name: "facility_name", label: "Facility / site name", type: "text", required: true },
      { name: "facility_address", label: "Site address", type: "text", required: true },
      { name: "facility_type", label: "Facility type", type: "select", options: FAC_TYPES, half: true },
      { name: "primary_business_activity", label: "Primary business activity on site", type: "text", half: true },
      { name: "floor_area", label: "Floor area", type: "text", half: true, hint: "sq m or sq ft" },
      { name: "number_of_sites", label: "Number of sites in portfolio", type: "text", half: true },
      { name: "number_of_employees", label: "Number of employees on site", type: "number", required: true, half: true },
      { name: "operating_hours", label: "Hours of operation", type: "text", required: true, half: true, hint: "e.g. 09:00–17:00, Mon–Fri" },
      { name: "how_many_floors", label: "How many floors?", type: "number", half: true },
      { name: "how_many_incomers", label: "How many incomers?", type: "number", half: true, hint: "electrical billing meters (main incomers)" },
      { name: "how_many_dbs", label: "How many distribution boards (DBs)?", type: "number", half: true },
      { name: "listed_status", label: "Listed / protected status", type: "text", half: true },
      { name: "backup_power", label: "Existing back-up power", type: "text", hint: "e.g. generator, UPS, none" },
      { name: "key_assets", label: "Key assets valuable to monitor", type: "textarea", hint: "e.g. major equipment, servers, air-conditioning, refrigeration — energy-intensive or business-critical" },
      { name: "facility_description", label: "Briefly describe the facility, its operational profile and any seasonal variation", type: "textarea" },
    ],
  },
  {
    title: "Current Energy Management",
    subtitle: "What you do today — what's measured, what's owned.",
    fields: [
      { name: "energy_policy", label: "Do you have a formal energy management policy, and if so, who owns it internally?", type: "textarea" },
      { name: "current_tools", label: "What tools, systems or platforms are currently used to monitor or manage energy?", type: "textarea" },
      { name: "what_measured", label: "What is measured today — invoices only, half-hourly data, sub-metering, real-time asset data?", type: "textarea" },
      { name: "review_frequency", label: "How often is energy performance reviewed, and by whom? What are the key decision points?", type: "textarea" },
      { name: "verify_results", label: "How did you previously verify and monitor — and what were the results?", type: "textarea" },
    ],
  },
  {
    title: "Business Challenges",
    subtitle: "Let's understand what the building is telling us — not what to buy. Knowledge before investment.",
    fields: [
      { name: "main_concerns", label: "What are your main concerns regarding energy, water or operational efficiency at this site?", type: "checkgroup", options: MAIN_CONCERNS, required: true, hint: "Select all that apply — it's fine to choose the last option." },
      { name: "main_pressures", label: "What are the main pressures driving the need to review your energy strategy?", type: "textarea", hint: "cost, carbon, capacity, comfort, growth, compliance, etc." },
      { name: "known_constraints", label: "Any known constraints — grid capacity, planning, listed status, tenant restrictions, operational continuity?", type: "textarea" },
      { name: "main_issues", label: "What are the main issues facing you as a company right now?", type: "textarea" },
    ],
  },
  {
    title: "Previous Investments",
    subtitle: "Anything already installed — when, by whom, and the perceived outcome.",
    fields: [
      { name: "inv_solar", label: "Solar PV", type: "text", hint: "size (kWp), year, installer" },
      { name: "inv_battery", label: "Battery Storage", type: "text", hint: "capacity (kWh), year, installer" },
      { name: "inv_ev", label: "EV Charging", type: "text", hint: "quantity, kW, year, installer" },
      { name: "inv_voltage", label: "Voltage Optimisation", type: "text", hint: "year, installer" },
      { name: "inv_led", label: "LED Lighting / Controls", type: "text", hint: "scope, year" },
      { name: "inv_hvac", label: "HVAC / BMS / Refrigeration upgrades", type: "text" },
      { name: "inv_other", label: "Anything else you've invested in to address energy consumption?", type: "textarea" },
    ],
  },
  {
    title: "Facility Data",
    subtitle: "The numbers we work from — leave blank if unknown, we'll help you find them.",
    groups: [
      {
        heading: "Energy Supply & Costs — Electricity",
        fields: [
          { name: "elec_annual_spend", label: "Annual electricity spend (£)", type: "text", half: true },
          { name: "elec_consumption", label: "Annual electricity consumption (kWh)", type: "text", half: true },
          { name: "elec_supplier", label: "Current electricity supplier", type: "text", half: true, hint: "supplier name, or 'Not sure'" },
          { name: "elec_unit_rate", label: "Electricity unit rate (pence per kWh)", type: "text", half: true, hint: "e.g. 24.5, or 'Not sure'" },
          { name: "elec_standing", label: "Daily standing charge (£/day)", type: "text", half: true, hint: "e.g. £1.20/day, or 'Not sure'" },
          { name: "elec_monthly_spend", label: "Monthly electricity spend (£)", type: "text", half: true },
          { name: "elec_peak", label: "Peak demand kVA (if known)", type: "text", half: true },
          { name: "elec_contract_end", label: "Contract end date", type: "text", half: true },
          { name: "elec_meter", label: "Meter type", type: "text", half: true, hint: "e.g. HH, AMR, smart" },
        ],
      },
      {
        heading: "Gas",
        fields: [
          { name: "gas_consumption", label: "Annual consumption (kWh)", type: "text", half: true },
          { name: "gas_annual_spend", label: "Annual spend (£)", type: "text", half: true },
          { name: "gas_monthly_spend", label: "Monthly spend (£)", type: "text", half: true },
          { name: "gas_unit_rate", label: "Unit rate (p/kWh)", type: "text", half: true },
          { name: "gas_standing", label: "Standing charge (p/day)", type: "text", half: true },
          { name: "gas_main_use", label: "Main use", type: "text", half: true, hint: "heating, process, DHW" },
          { name: "gas_supplier", label: "Main supplier", type: "text", half: true },
          { name: "gas_contract_end", label: "Contract end date", type: "text", half: true },
          { name: "gas_meter", label: "Meter type", type: "text", half: true },
        ],
      },
      {
        heading: "Water",
        fields: [
          { name: "water_consumption", label: "Annual consumption (m³)", type: "text", half: true },
          { name: "water_annual_spend", label: "Annual spend (£)", type: "text", half: true },
          { name: "water_monthly_spend", label: "Monthly spend (£)", type: "text", half: true },
          { name: "water_unit_rate", label: "Unit rate (£/m³)", type: "text", half: true },
          { name: "water_main_use", label: "Main use", type: "text", half: true },
          { name: "water_supplier", label: "Main supplier", type: "text", half: true },
        ],
      },
    ],
    fields: [
      { name: "bill", label: "Upload a recent electricity, gas or water bill (optional)", type: "bill", hint: "PDF or photo · a bill often tells us the tariff, standing charge and consumption more accurately than memory." },
    ],
  },
  {
    title: "Almost Done",
    subtitle: "A couple of final details.",
    fields: [
      { name: "how_heard", label: "How did you hear about us?", type: "select", options: HEARD, half: true },
      { name: "preferred_contact", label: "Preferred contact method", type: "select", options: CONTACT_PREF, half: true },
      { name: "best_time", label: "Best time to contact you", type: "text" },
      { name: "consent", label: "I'm happy for Energy Vault Group to contact me about my complimentary Gap Analysis.", type: "checkbox", required: true },
    ],
  },
];

function stepFields(step) {
  return [...((step.groups || []).flatMap((g) => g.fields)), ...(step.fields || [])];
}

function Nav() {
  return (
    <nav className="evnav">
      <div className="wrap navin">
        <a href="/"><Brand /></a>
        <div className="navlinks">
          <a href="/">Home</a>
          <a href="/how-it-works">How It Works</a>
          <a href="/#contact" className="btn" style={{ padding: "10px 20px" }} data-testid="ga-nav-cta">Talk to Us</a>
        </div>
      </div>
    </nav>
  );
}

const BILL_MAP = {
  electricity: { supplier: "elec_supplier", unit_rate: "elec_unit_rate", standing_charge: "elec_standing", annual_consumption: "elec_consumption", annual_spend: "elec_annual_spend", contract_end: "elec_contract_end" },
  gas: { supplier: "gas_supplier", unit_rate: "gas_unit_rate", standing_charge: "gas_standing", annual_consumption: "gas_consumption", annual_spend: "gas_annual_spend", contract_end: "gas_contract_end" },
  water: { supplier: "water_supplier", unit_rate: "water_unit_rate", annual_consumption: "water_consumption", annual_spend: "water_annual_spend" },
};

function BillField({ f, form, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [reading, setReading] = useState(false);

  const readBill = async (billPath) => {
    setReading(true);
    try {
      const { data } = await axios.post(`${API}/gap-analysis/extract-bill`, { bill_path: billPath });
      const ex = data.extracted || {};
      const map = BILL_MAP[ex.utility];
      if (data.ok && map) {
        const filled = [];
        Object.entries(map).forEach(([k, field]) => { if (ex[k]) { onChange(field, ex[k]); filled.push(field); } });
        onChange("_aiFields", filled);
        onChange("_billUtility", ex.utility);
        if (filled.length) toast.success("We read your bill — please review the highlighted figures above.");
        else toast("Couldn't read details automatically — please enter them manually.");
      } else {
        toast("Couldn't read the bill automatically — please enter the details manually.");
      }
    } catch (err) {
      toast("Couldn't read the bill automatically — please enter the details manually.");
    } finally {
      setReading(false);
    }
  };

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("File too large (max 15MB)."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/gap-analysis/upload-bill`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange("bill_path", data.bill_path);
      onChange("bill_filename", data.bill_filename);
      toast.success("Bill uploaded.");
      await readBill(data.bill_path);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed, please try again.");
    } finally {
      setUploading(false);
    }
  };
  const remove = () => { onChange("bill_path", ""); onChange("bill_filename", ""); onChange("_aiFields", []); onChange("_billUtility", ""); };
  return (
    <div className="ga-field" style={{ gridColumn: "span 2", marginTop: 6 }}>
      <label>{f.label}</label>
      {f.hint && <div className="ga-hint" style={{ marginTop: 0, marginBottom: 10 }}>{f.hint}</div>}
      {form.bill_filename ? (
        <div className="ga-billfile" data-testid="ga-bill-file">
          <span className="ga-billname">{form.bill_filename}{reading ? "  ·  reading…" : ""}</span>
          <button type="button" className="ga-billremove" onClick={remove} data-testid="ga-bill-remove" disabled={reading}>Remove</button>
        </div>
      ) : (
        <label className={`ga-billdrop${uploading ? " busy" : ""}`} data-testid="ga-bill-drop">
          <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,image/*,application/pdf" onChange={handle} disabled={uploading} data-testid="ga-bill" style={{ display: "none" }} />
          {uploading ? "Uploading…" : "Choose a file or drag it here"}
        </label>
      )}
      {reading && <div className="ga-hint" data-testid="ga-bill-reading" style={{ marginTop: 8, color: "var(--teal)" }}>Reading your bill with AI… we'll pre-fill what we can find.</div>}
    </div>
  );
}

function Field({ f, value, onChange, form }) {
  const common = { "data-testid": `ga-${f.name}`, value: value || "", onChange: (e) => onChange(f.name, e.target.value) };
  if (f.type === "bill") {
    return <BillField f={f} form={form} onChange={onChange} />;
  }
  if (f.type === "checkbox") {
    return (
      <label className="ga-check" data-testid={`ga-${f.name}-label`}>
        <input type="checkbox" data-testid={`ga-${f.name}`} checked={!!value} onChange={(e) => onChange(f.name, e.target.checked)} />
        <span>{f.label}{f.required && <b className="req"> *</b>}</span>
      </label>
    );
  }
  if (f.type === "checkgroup") {
    const selected = Array.isArray(value) ? value : [];
    const toggle = (opt) => onChange(f.name, selected.includes(opt) ? selected.filter((o) => o !== opt) : [...selected, opt]);
    return (
      <div className="ga-field" style={{ gridColumn: "span 2" }}>
        <label>{f.label}{f.required && <b className="req"> *</b>}</label>
        {f.hint && <div className="ga-hint" style={{ marginTop: 0, marginBottom: 10 }}>{f.hint}</div>}
        <div className="ga-checkgroup" data-testid={`ga-${f.name}`}>
          {f.options.map((opt) => (
            <button
              type="button"
              key={opt}
              className={`ga-chip${selected.includes(opt) ? " on" : ""}`}
              onClick={() => toggle(opt)}
              data-testid={`ga-${f.name}-opt-${opt.split(" ")[0].toLowerCase().replace(/[^a-z]/g, "")}`}
            >
              <span className="ga-chipbox" />{opt}
            </button>
          ))}
        </div>
      </div>
    );
  }
  const aiFilled = (form?._aiFields || []).includes(f.name);
  return (
    <div className={`ga-field${f.half ? " half" : ""}${aiFilled ? " ai" : ""}`}>
      <label>{f.label}{f.required && <b className="req"> *</b>}{aiFilled && <span className="ga-aitag" data-testid={`ga-ai-${f.name}`}>AI-read · check</span>}</label>
      {f.type === "textarea" ? (
        <textarea rows={3} placeholder={f.hint || ""} {...common} />
      ) : f.type === "select" ? (
        <select {...common}>
          <option value="">Please select…</option>
          {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input type={f.type} placeholder={f.hint || ""} {...common} />
      )}
      {f.hint && f.type !== "textarea" && f.type !== "select" && <div className="ga-hint">{f.hint}</div>}
    </div>
  );
}

export default function GapAnalysisForm() {
  const draft = (() => { try { return JSON.parse(localStorage.getItem(DRAFT_KEY)) || {}; } catch { return {}; } })();
  const [step, setStep] = useState(draft.step || 0);
  const [form, setForm] = useState(draft.form || {});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const total = STEPS.length;
  const cur = STEPS[step];
  const set = (name, val) => setForm((p) => ({ ...p, [name]: val }));

  const hasDraft = Object.keys(form).length > 0;
  useEffect(() => {
    if (!done) localStorage.setItem(DRAFT_KEY, JSON.stringify({ step, form }));
  }, [step, form, done]);

  const startOver = () => {
    if (!window.confirm("Clear your saved answers and start over?")) return;
    localStorage.removeItem(DRAFT_KEY);
    setForm({});
    setStep(0);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const validateStep = () => {
    for (const f of stepFields(cur)) {
      if (!f.required) continue;
      const v = form[f.name];
      const empty = f.type === "checkbox" ? !v : f.type === "checkgroup" ? !(Array.isArray(v) && v.length) : !String(v || "").trim();
      if (empty) {
        toast.error(`Please complete: ${f.label.replace(/\?.*/, "?")}`);
        return false;
      }
    }
    return true;
  };

  const next = () => { if (validateStep()) { setStep((s) => Math.min(s + 1, total - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); } };
  const back = () => { setStep((s) => Math.max(s - 1, 0)); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const buildSections = () =>
    STEPS.slice(0, total - 1).map((s) => ({
      title: s.title,
      fields: stepFields(s)
        .filter((f) => f.type !== "checkbox" && f.type !== "bill")
        .map((f) => ({ label: f.label, value: Array.isArray(form[f.name]) ? form[f.name].join(", ") : String(form[f.name] ?? "") })),
    }));

  const submit = async () => {
    if (!validateStep()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/gap-analysis`, {
        client_first_name: form.client_first_name,
        client_last_name: form.client_last_name || "",
        client_email: form.client_email,
        company_name: form.company_name || "",
        sections: buildSections(),
        bill_path: form.bill_path || null,
        bill_filename: form.bill_filename || null,
      });
      localStorage.removeItem(DRAFT_KEY);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error("Something went wrong. Please try again or email michael@energyvaultgroup.co.uk");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="evg gapage">
      <Nav />
      <header className="gahero">
        <div className="wrap">
          <img className="galogo" src="/brand/logo.png" alt="Energy Vault Group" data-testid="ga-logo" />
          <div className="eyebrow" style={{ textAlign: "left" }}>KNOWLEDGE BEFORE INVESTMENT</div>
          <h1>Building Intelligence <span className="g">Gap Analysis</span></h1>
          <p className="galead">Complete this short questionnaire and our team will prepare your complimentary, no-cost analysis. Leave anything blank if you're unsure — we'll help you find it.</p>
        </div>
      </header>

      <section className="gawrap">
        <div className="wrap">
          {done ? (
            <div className="ga-done" data-testid="ga-success">
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#22A9E0" strokeWidth="2" style={{ margin: "0 auto" }}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
              <h2>Thank you, {form.client_first_name}.</h2>
              <p>Your Gap Analysis has been submitted. Our team will review your answers and be in touch shortly — and a confirmation is on its way to your inbox.</p>
              <a href="/" className="btn" data-testid="ga-home">Back to Home</a>
            </div>
          ) : (
            <div className="ga-card">
              <div className="ga-progress" data-testid="ga-progress">
                <div className="ga-steps">Step {step + 1} of {total} · <span className="g">{cur.title}</span></div>
                <div className="ga-bar"><div className="ga-fill" style={{ width: `${((step + 1) / total) * 100}%` }} /></div>
              </div>

              <h3 className="ga-title">{cur.title}</h3>
              {cur.subtitle && <p className="ga-sub">{cur.subtitle}</p>}

              {cur.title === "Facility Data" && (form._aiFields || []).length > 0 && (
                <div className="ga-aibanner" data-testid="ga-ai-banner">
                  <b>We've read your uploaded bill and filled in what we could.</b> Please check and correct every figure below before submitting — AI can occasionally misread numbers, and your Gap Analysis should start from facts you've confirmed.
                </div>
              )}

              {cur.groups && cur.groups.map((g) => (
                <div key={g.heading} className="ga-group">
                  <div className="ga-group-h">{g.heading}</div>
                  <div className="ga-grid">
                    {g.fields.map((f) => <Field key={f.name} f={f} value={form[f.name]} onChange={set} form={form} />)}
                  </div>
                </div>
              ))}
              {cur.fields && (
                <div className="ga-grid">
                  {cur.fields.map((f) => <Field key={f.name} f={f} value={form[f.name]} onChange={set} form={form} />)}
                </div>
              )}

              <div className="ga-nav">
                {step > 0 && <button className="btn ghost" onClick={back} data-testid="ga-back" disabled={loading}>Back</button>}
                <div style={{ flex: 1 }} />
                {step < total - 1 ? (
                  <button className="btn" onClick={next} data-testid="ga-next">Continue</button>
                ) : (
                  <button className="btn" onClick={submit} data-testid="ga-submit" disabled={loading}>{loading ? "Submitting…" : "Submit Gap Analysis"}</button>
                )}
              </div>

              {hasDraft && (
                <div className="ga-saved" data-testid="ga-saved">
                  <span>Your answers are saved automatically on this device — pop back any time to finish.</span>
                  <button type="button" onClick={startOver} data-testid="ga-startover">Start over</button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}
